import "./StepManager.js"
import { getFinishStep } from '../../helpers/surveyFinishSteps';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment-timezone';
import getStepViewLogger from "../../api/survey/surveyAnalytics";
import { calculatedFieldStepIdentifier, evaluateCalculatedFields, evaluatePredicateExpression } from "../../components/Survey/Util/calculatedFields";
import { evaluatePredicate } from "../../helpers/surveyPredicates";
import {
	deleteConsentRestorationData,
	deleteRestorationData,
	persistConsentRestorationData,
	persistRestorationData
} from '../../api/survey/restorationData';

function SurveyManager($rootScope, stepManagerFactory, parameters, options) {

	this.$rootScope = $rootScope;
	this.stepManagerFactory = stepManagerFactory;
	this.stepViewLogger = options.stepViewLogger ? options.stepViewLogger : getStepViewLogger();

	this.surveyID = parameters.surveyID;
	this.surveySessionID = parameters.surveySessionID;
	this.template = parameters.template;
	this.participantID = parameters.participantID;
	this.taskID = parameters.taskID;
	this.followUp = parameters.followUp;
	this.projectRecordAuthorityCode = parameters.projectRecordAuthorityCode;
	this.projectID = parameters.projectID;
	this.templateVersion = parameters.templateVersion;
	this.participantStateAudit = parameters.participantStateAudit;
	this.finishedCallback = parameters.finishedCallback;
	this.saveResultsCallback = parameters.saveResultsCallback;
	this.saveResultsErrorCallback = parameters.saveResultsErrorCallback;
	this.restorationData = parameters.restorationData;
	this.replaceResultID = parameters.replaceResultID;
	this.expressionInputs = parameters.expressionInputs;
	this.event = parameters.event;
	this.isCoordinatorEntered = options.isCoordinatorEntered;
	this.analyticsEnabled = options.analyticsEnabled;
	this.context = options.context ? options.context : (this.taskID ? 'TaskBased' : 'NoTask');

	this.taskRunUUID = uuidv4();
	this.managedResults = {};
	this.managedStepIdentifiers = [];
	this.startDate = null;
	this.endDate = null;
	this.currentStepManager = null;

	this.storeProgress = options.storeProgress;
	this.linkSurvey = options.linkSurvey || false;

	if (parameters.existingResult) {
		parameters.existingResult.StepResults.forEach((sr) => {
			this.managedResults[sr.Identifier] = sr;
		});
	}

	this.mode = "Survey";
	if (!_.isUndefined(options.mode)) {
		this.mode = options.mode;
	}
	checkMode(this.mode);
}

SurveyManager.prototype.startSurvey = function (firstStepIndex) {
	if (this.restorationData) {
		try {
			this.startDate = this.restorationData.startDate;
			this.managedResults = this.restorationData.managedResults;
			this.managedStepIdentifiers = this.restorationData.managedStepIdentifiers;
			this.taskRunUUID = this.restorationData.taskRunUUID;
			var currentStepIdentifier = this.managedStepIdentifiers[this.managedStepIdentifiers.length - 1];
			var step = _.findWhere(this.template.steps, { identifier: currentStepIdentifier });
			this.setStep(step, true);
		} catch (e) { }
	}

	if (!this.currentStepManager) {
		this.startDate = moment().toISOString(true);
		let firstStep;
		if (firstStepIndex && firstStepIndex < this.template.steps.length) {
			firstStep = this.template.steps[firstStepIndex];
		} else {
			firstStep = this.nextStep();
		}
		if (!firstStep) {
			this.endAndComplete();
		} else {
			this.setStep(firstStep, true);
			this.managedStepIdentifiers.push(firstStep.identifier);
		}
	}
};

SurveyManager.prototype.getResult = function () {
	var stepResults = [];
	var documents = [];
	for (var i = 0; i < this.managedStepIdentifiers.length; i++) {
		var stepIdentifier = this.managedStepIdentifiers[i];
		var key = uniqueManagedKey(stepIdentifier, i);

		if (this.managedResults[key] === undefined && this.managedResults[stepIdentifier] === undefined) continue;
		let stepResult = (this.managedResults[key] || this.managedResults[stepIdentifier]);
		let stepResultSlim = { ...stepResult };
		delete stepResultSlim.Documents;
		stepResults.push(stepResultSlim);
		documents = documents.concat(stepResult.Documents || []);
	}

	if (this.managedResults[calculatedFieldStepIdentifier]) {
		stepResults.push(this.managedResults[calculatedFieldStepIdentifier]);
	}

	return {
		StartDate: this.startDate,
		EndDate: this.endDate,
		TaskRunUUID: this.taskRunUUID,
		StepResults: stepResults,
		TaskID: this.taskID,
		SurveyID: this.surveyID,
		ParticipantID: this.participantID,
		SurveyVersion: this.templateVersion,
		DeviceInformation: {
			Platform: "WebClient",
			DeviceMetrics: {
				UserAgent: navigator.userAgent
			},
			TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			Offset: moment().format("Z")
		},
		Documents: documents,
		ParticipantStateAudit: this.participantStateAudit,
		ReplaceResultID: this.replaceResultID,
		Event: this.event
	};
};

SurveyManager.prototype.getActiveManagedResults = function () {
	var activeManagedResults = {};
	for (var i = 0; i < this.managedStepIdentifiers.length; i++) {
		var stepIdentifier = this.managedStepIdentifiers[i];
		activeManagedResults[stepIdentifier] = this.managedResults[stepIdentifier];
	}
	return activeManagedResults;
};

SurveyManager.prototype.getCompletionAction = function () {
	var completionAction = this.template.completionAction;
	if (!completionAction) {
		return null;
	}
	completionAction.patientID = this.participantID;
	if (completionAction.type == "StartParticipantSurvey" ||
		completionAction.type == "ShowParticipantDashboard" ||
		completionAction.type == "ShowTab" ||
		completionAction.type == "OpenExternalLink" ||
		completionAction.type == "ShowParticipantWebVisualization") {
		return completionAction;
	}

	//all other actions not allowed
	return null;
};

SurveyManager.prototype.stepCanContinue = function (stepManager) {
	var activeManagedResults = this.getActiveManagedResults();
	var base = this;
	var violatedRules = _.filter(stepManager.step.validationRules,
		function (rule) { return evaluatePredicate(rule.resultPredicate, activeManagedResults, base.getPredicateExpressionEvaluator()); });
	var errorMessages = _.pluck(violatedRules, "errorMessage");
	if (errorMessages.length) {
		alert(_.first(errorMessages));
		return false;
	}
	if (stepManager.canContinue) {
		if (!stepManager.canContinue()) return false;
	}
	return true;
};

SurveyManager.prototype.stepDidFinish = function (stepManager, navigatingForwards) {
	if (navigatingForwards) {
		this.flipToNextPageFrom(stepManager);
	} else {
		this.flipToPreviousPageFrom(stepManager);
	}
};

SurveyManager.prototype.flipToNextPageFrom = function (stepManager) {
	if (this.currentStepManager !== stepManager) {
		return;
	}

	if (this.currentStepManager.step.saveOnArrival) {
		this.endAndComplete();
		return;
	}

	var step = this.nextStep();
	if (step === null) {
		this.endAndComplete();
	} else if (getFinishStep(step.identifier)) {
		if (step.identifier == 'FinishStep.Cancel') {
			this.promptForSurveyClose();
		} else if (step.identifier == 'FinishStep.CancelAndSave') {
			this.saveProgressAndClose();
		} else if (step.identifier == 'FinishStep.CancelAndDiscard') {
			this.deleteProgressAndClose();
		} else { // 'FinishStep.Complete'
			this.endAndComplete();
		}
	}
	else {
		this.managedStepIdentifiers.push(step.identifier);
		var resultKey = uniqueManagedKey(step.identifier, this.managedStepIdentifiers.length - 1);
		this.setStep(step, true, resultKey);
	}
};

SurveyManager.prototype.flipToPreviousPageFrom = function (stepManager) {
	if (this.currentStepManager !== stepManager) {
		return;
	}

	var previousStep = this.previousStep();
	this.managedStepIdentifiers.pop();
	var resultKey = uniqueManagedKey(previousStep.identifier, this.managedStepIdentifiers.length - 1);
	this.setStep(previousStep, false, resultKey);
};

SurveyManager.prototype.finishSaveBeforeEndOfSurveyStep = function () {
	return !!this.linkSurvey;
}

SurveyManager.prototype.setStep = function (step, navigatingForwards, resultKey) {
	if (!step) { return null; }

	this.currentStepManager = this.stepManagerFactory.createStepManager(step, this);
	var previousResult = this.managedResults[step.identifier];
	if (resultKey && this.managedResults[resultKey]) {
		previousResult = this.managedResults[resultKey];
	}
	this.currentStepManager.initializeResults(previousResult, navigatingForwards);

	this.trackStepViewed(this.currentStepManager.step.identifier);
	this.saveProgress();
	if (!this.editMode && step.saveOnArrival) {
		this.endDate = moment().toISOString(true);
		if (this.finishSaveBeforeEndOfSurveyStep()) {
			this.finishing = true;
			this.savePromise = this.saveResultsCallback().then(() => {
				this.finishing = false;
				this.$rootScope.$apply();
			});
		} else {
			this.savePromise = this.saveResultsCallback();
		}
	}
	this.$rootScope.$broadcast("setStep", step);
};

SurveyManager.prototype.getNextStepViewedEvent = function (stepIdentifier) {
	var event = {
		SurveyID: this.surveyID,
		SurveySessionID: this.surveySessionID,
		TaskID: this.taskID,
		SurveyVersion: this.templateVersion,
		Context: this.context,
		ClientLocalTimestamp: moment().toISOString(true),
		EventID: uuidv4(),
		StepIdentifier: stepIdentifier,
		TaskRunUUID: this.taskRunUUID
	};
	if (this.participantID) {
		event.ParticipantID = this.participantID;
	}
	if (this.projectRecordAuthorityCode) {
		event.projectRecordAuthorityCode = this.projectRecordAuthorityCode;
	}
	if (this.previousEventID) {
		event.PreviousEventID = this.previousEventID;
	}

	this.previousEventID = event.EventID;

	return event;
};

SurveyManager.prototype.getNextSubStepViewedEvent = function (stepIdentifier, subStepIdentifier, subStepIndex) {
	var event = this.getNextStepViewedEvent(stepIdentifier);
	event.SubStepIdentifier = subStepIdentifier;
	event.SubStepIndex = subStepIndex;
	return event;
};

SurveyManager.prototype.trackStepViewed = function (stepIdentifier) {
	if (this.analyticsEnabled && ["ConsentReviewStep", "VisualConsentStep"].indexOf(this.currentStepManager.step.type) === -1) {
		this.stepViewLogger(this.getNextStepViewedEvent(stepIdentifier));
	}
};

SurveyManager.prototype.trackSubStepViewed = function (stepIdentifier, subStepIdentifier, subStepIndex) {
	if (this.analyticsEnabled) {
		this.stepViewLogger(this.getNextSubStepViewedEvent(stepIdentifier, subStepIdentifier, subStepIndex));
	}
};

SurveyManager.prototype.previousStep = function () {
	var stepIdentifier = this.managedStepIdentifiers[this.managedStepIdentifiers.length - 2];
	return this.template.steps[getStepIndex(this.template, stepIdentifier)];
};

SurveyManager.prototype.nextStep = function () {
	let activeManagedResults = this.getActiveManagedResults();
	let currentStepManager = this.currentStepManager;

	let nextStep = stepAfterStep(currentStepManager, this.template, activeManagedResults, this.editMode, undefined, this.getPredicateExpressionEvaluator());
	let skipNextStep = nextStep?.skipNavigationRule ? evaluatePredicate(nextStep.skipNavigationRule.resultPredicate, activeManagedResults, this.getPredicateExpressionEvaluator()) : false;

	while (skipNextStep) {
		currentStepManager = this.stepManagerFactory.createStepManager(nextStep, this);
		nextStep = stepAfterStep(currentStepManager, this.template, activeManagedResults, this.editMode, this.template.skipProcessingNavigationRulesForSkippedSteps && this.template.useWebSurveyEngine, this.getPredicateExpressionEvaluator());
		if (!this.editMode && nextStep && nextStep.skipNavigationRule) {
			skipNextStep = evaluatePredicate(nextStep.skipNavigationRule.resultPredicate, activeManagedResults, this.getPredicateExpressionEvaluator());
		} else {
			skipNextStep = false;
		}
	}

	return nextStep;
};

SurveyManager.prototype.setManagedResult = function (result, stepIdentifier) {
	this.managedResults[stepIdentifier] = result;

	var index = this.managedStepIdentifiers.length;
	if (this.managedStepIdentifiers[index - 1] === stepIdentifier) {
		index = index - 1;
	}
	this.managedResults[uniqueManagedKey(stepIdentifier, index)] = result;

	if (this.template.calculatedFields?.length > 0 && this.mode !== "Design" && this.expressionInputs) {
		const evaluation = evaluateCalculatedFields(this.template, this.expressionInputs, this.getResult());
		if (evaluation) {
			this.managedResults[calculatedFieldStepIdentifier] = evaluation.stepResult;
			if (this.isCoordinatorEntered || this.mode === "Preview") {
				this.$rootScope.$broadcast("calculatedFieldsEvaluated", evaluation.fieldSnapshots);
			}
		}
	}
};

SurveyManager.prototype.close = function (reason) {
	this.endDate = moment().toISOString(true);
	this.finishedCallback(reason || "Closed");
};

SurveyManager.prototype.showSaveAndClose = function () {
	var hasWebViewStep = _.some(this.template.steps, { type: "WebViewStep" });
	return this.isCoordinatorEntered && !hasWebViewStep;
};

SurveyManager.prototype.saveAndClose = function () {
	var surveyManager = this;
	var interval = setInterval(function () {
		if (surveyManager.endDate) {
			window.clearInterval(interval);
			return;
		}
		var continued = surveyManager.currentStepManager.continue();
		if (!continued) {
			window.clearInterval(interval);
			surveyManager.$rootScope.$apply();
		}
	}, 10);
};

SurveyManager.prototype.getMode = function () {
	return this.mode;
};

SurveyManager.prototype.promptForSurveyClose = function () {
	var surveyManager = this;
	if (shouldStoreProgress(this) && !this.template.neverShowCancelPrompt) {
		this.showSaveProgressPrompt = true;
		this.$rootScope.$broadcast("refocus");
	} else {
		surveyManager.close("Discarded");
	}
};

SurveyManager.prototype.saveProgressAndClose = function () {
	var surveyManager = this;
	if (surveyManager.taskID && surveyManager.managedStepIdentifiers.length > 1) {
		this.saveProgress(function (success) {
			if (success) {
				surveyManager.close("Saved");
			} else {
				surveyManager.close("Failed");
			}
		});
	}
	else {
		surveyManager.close("Discarded");
	}
}

SurveyManager.prototype.deleteProgressAndClose = function () {
	var surveyManager = this;
	if (surveyManager.taskID) {
		deleteRestorationData(surveyManager.taskID).then(function () {
			surveyManager.close("Discarded");
		}).catch(function () {
			surveyManager.close("Failed");
		});
	} else if (surveyManager.context == 'Consent') {
		deleteConsentRestorationData(surveyManager.projectID).then(function () {
			surveyManager.close("Discarded");
		}).catch(function () {
			surveyManager.close("Failed");
		});
	}
	else {
		surveyManager.close("Discarded");
	}
}

SurveyManager.prototype.saveProgress = function (callback) {
	if (!shouldStoreProgress(this)) { return; }

	if (this.context != 'Consent') {
		persistRestorationData(this.taskID, {
			taskRunUUID: this.taskRunUUID,
			managedStepIdentifiers: this.managedStepIdentifiers,
			managedResults: this.managedResults,
			startDate: this.startDate
		}).then(function () {
			if (callback) {
				callback(true);
			}
		}).catch(function () {
			if (callback) {
				callback(false);
			}
		});
	} else {
		persistConsentRestorationData(this.projectID, {
			taskRunUUID: this.taskRunUUID,
			templateVersion: this.templateVersion,
			managedStepIdentifiers: this.managedStepIdentifiers,
			managedResults: this.managedResults,
			startDate: this.startDate
		}).then(function () {
			if (callback) {
				callback(true);
			}
		}).catch(function () {
			if (callback) {
				callback(false);
			}
		});
	}
}

SurveyManager.prototype.resume = function () {
	this.showSaveProgressPrompt = false;
	this.$rootScope.$broadcast("refocus");
}

SurveyManager.prototype.endAndComplete = function () {
	var surveyManager = this;
	this.endDate = moment().toISOString(true);

	// this.savePromise is already set if there was a saveOnArrivalStep.
	// Otherwise, trigger the save now.
	this.finishing = true;
	this.savePromise = this.savePromise || (this.saveResultsCallback ? this.saveResultsCallback() : Promise.resolve());

	this.savePromise.then(function () {
		if (surveyManager.finishedCallback) {
			surveyManager.finishedCallback("Completed")
		}
	}).catch(function (e) {
		if (surveyManager.saveResultsErrorCallback) {
			surveyManager.saveResultsErrorCallback(e)
		}
		else {
			// ensure error is propagated if no handler
			return Promise.reject(e);
		}
	}).finally(function () {
		surveyManager.finishing = false;
		surveyManager.savePromise = null;
	});
}

SurveyManager.prototype.finishing = false;

SurveyManager.prototype.getPredicateExpressionEvaluator = function () {
	if (this.mode === "Design" || !this.expressionInputs || !this.expressionInputs.currentSurveyContext) {
		return () => false; //don't evaluate predicates in design mode
	}
	return (expression) => evaluatePredicateExpression(expression, this.template, this.expressionInputs, this.getResult());
}

function checkMode(mode) {
	switch (mode) {
		case "Design":
		case "Survey":
		case "Preview":
			return;
		default:
			throw "Mode must be Design, Survey, or Preview";
	}
}

function uniqueManagedKey(stepIdentifier, index) {
	return stepIdentifier + ":" + index;
}

function stepAfterStep(stepManager, template, activeManagedResults, editMode, skipProcessingNavigationRules, predicateExpressionEvaluator) {
	if (!stepManager) {
		return template.steps[0];
	}
	var defaultNextStepIndex = getStepIndex(template, stepManager.step.identifier) + 1;
	var defaultNextStep = null;
	if (defaultNextStepIndex < template.steps.length) {
		defaultNextStep = template.steps[defaultNextStepIndex];
	}

	if (template.type === 'RKStudioOrderedTask' || skipProcessingNavigationRules) {
		return defaultNextStep;
	} else if (template.type === 'RKStudioNavigableOrderedTask') {
		var navigationRule = stepManager.step.navigationRule;
		if (!navigationRule) {
			return defaultNextStep;
		}

		switch (navigationRule.type) {
			case "DirectStepNavigationRule":
				return lookupNextStep(template.steps, navigationRule.destinationStepIdentifier);
			case "PredicateStepNavigationRule":
				//don't evaluate predicates in edit mode
				if (editMode) { return defaultNextStep; }

				var firstTruePredicate = _.find(navigationRule.resultPredicateDestinations,
					function (predicateDestination) { return evaluatePredicate(predicateDestination.resultPredicate, activeManagedResults, predicateExpressionEvaluator); });
				var nextStepIdentifier = navigationRule.defaultStepIdentifier;
				if (firstTruePredicate) {
					nextStepIdentifier = firstTruePredicate.destinationStepIdentifier;
				}
				if (!nextStepIdentifier) {
					return defaultNextStep;
				}
				return lookupNextStep(template.steps, nextStepIdentifier);
			default:
				throw "Unsupported NavigationRule type: " + navigationRule.type;
		}
	}
}

function lookupNextStep(steps, identifier) {
	var finishStep = getFinishStep(identifier);
	if (finishStep) {
		return finishStep;
	} else {
		return _.findWhere(steps, { identifier: identifier });
	}
}

function getStepIndex(template, identifier) {
	return _.findIndex(template.steps, function (step) { return step.identifier === identifier; });
}

function shouldStoreProgress(surveyManager) {
	var currentResult = surveyManager.getResult();
	return !surveyManager.template.neverStoreProgress && surveyManager.storeProgress && (surveyManager.context == 'Consent' || surveyManager.taskID) && surveyManager.managedStepIdentifiers.length > 1 &&
		!currentResult.StepResults.some(stepResult => stepResult.QuestionResults.some(q => q.Type === "FileResult" && q.File));
}

angular.module('ResearchKitApplicationModule').factory('surveyManagerFactory', ["$rootScope", "stepManagerFactory", function ($rootScope, stepManagerFactory) {
	var defaultOptions = {
		storeProgress: true,
		linkSurvey: false
	};

	function createSurveyManager(parameters, options) {
		var accumulatedOptions = $.extend({}, defaultOptions, options);
		return new SurveyManager($rootScope, stepManagerFactory, parameters, accumulatedOptions);
	}

	return {
		createSurveyManager: createSurveyManager
	};
}]);